/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Fiscal-only segmented control: active = brand teal fill + white text.
 * Do not use for POS/Customer operational filters.
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';

export type FiscalBrandFillOption<T extends string> = {
  value: T;
  label: string;
  shortLabel?: string;
  /** Línea corta debajo del título (p. ej. qué factura emite el régimen). */
  subtitle?: string;
};

type Props<T extends string> = {
  options: Array<FiscalBrandFillOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Vertical stack (wide PC layout for régimen). */
  vertical?: boolean;
  accessibilityLabel?: string;
};

export function FiscalBrandFillSegmented<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  vertical = false,
  accessibilityLabel,
}: Props<T>) {
  return (
    <View
      style={[styles.track, vertical && styles.trackVertical, disabled && styles.disabled]}
      accessibilityRole="radiogroup"
      accessibilityLabel={accessibilityLabel}
    >
      {options.map((option) => {
        const active = value === option.value;
        return (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.segment,
              vertical && styles.segmentVertical,
              active && styles.segmentActive,
            ]}
            onPress={() => {
              if (!disabled) onChange(option.value);
            }}
            disabled={disabled}
            accessibilityRole="radio"
            accessibilityState={{ selected: active, disabled }}
            accessibilityLabel={option.label}
          >
            <Text
              style={[styles.segmentText, active && styles.segmentTextActive, vertical && styles.segmentTextVertical]}
              numberOfLines={2}
            >
              {option.label}
            </Text>
            {option.subtitle ? (
              <Text
                style={[
                  styles.segmentSubtext,
                  active && styles.segmentSubtextActive,
                  vertical && styles.segmentTextVertical,
                ]}
                numberOfLines={2}
              >
                {option.subtitle}
              </Text>
            ) : null}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: 3,
    gap: 3,
  },
  trackVertical: {
    flexDirection: 'column',
  },
  segment: {
    flex: 1,
    minHeight: 42,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
    backgroundColor: 'transparent',
  },
  segmentVertical: {
    minHeight: 44,
    alignItems: 'flex-start',
    paddingHorizontal: 14,
  },
  segmentActive: {
    backgroundColor: ticketColors.gold,
    shadowColor: ticketColors.gold,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 3,
    elevation: 2,
  },
  segmentText: {
    ...typography.caption,
    fontWeight: '600',
    fontSize: 13,
    color: ticketColors.ink2,
  },
  segmentTextVertical: {
    textAlign: 'left',
  },
  segmentTextActive: {
    color: ticketColors.goldInk,
  },
  segmentSubtext: {
    ...typography.caption,
    fontSize: 11,
    marginTop: 2,
    color: ticketColors.ink3,
  },
  segmentSubtextActive: {
    color: ticketColors.goldInk,
    opacity: 0.85,
  },
  disabled: { opacity: 0.55 },
});
